import { ComponentRef, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from "@angular/router";
import { ASSET_ID_ROUTE_PARAM_NAME, ROADWORK_ID_ROUTE_PARAM_NAME } from "src/app/config/routing.config";
import { DigitalRoadworkService } from "src/app/services/digital-roadwork.service";

/**
 * Route reuse strategy that saves the state of components when navigating through the application.
 */
@Injectable({
	providedIn: "root",
	deps: [DigitalRoadworkService],
})
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
	constructor(private readonly digitalRoadworkService: DigitalRoadworkService) {
		this.digitalRoadworkService.idOfCurrentDigitalEquipmentFromUser.subscribe(() => {
			this.handlers = {};
		});
	}

	private handlers: { [key: string]: DetachedRouteHandle } = {};

	public shouldDetach(route: ActivatedRouteSnapshot): boolean {
		return !route.routeConfig?.loadChildren;
	}

	public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
		if (this.getKey(route) && handle) {
			this.handlers[this.getKey(route)] = handle;
		}
	}

	public shouldAttach(route: ActivatedRouteSnapshot): boolean {
		if (!this.getKey(route)) {
			return false;
		}
		return !route.routeConfig?.loadChildren && !!this.handlers[this.getKey(route)];
	}

	public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
		if (!this.getKey(route)) {
			return null;
		}
		return this.handlers[this.getKey(route)];
	}

	public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
		if (!this.getKey(future) || !this.getKey(curr)) {
			return false;
		}
		return this.getKey(future) === this.getKey(curr);
	}

	/** Clears and destroys all cached components. */
	public clearHandlers(): void {
		for (const key in this.handlers) {
			const handle = this.handlers[key];
			const componentRef: ComponentRef<unknown> = (<any>handle)["componentRef"];
			if (componentRef) {
				componentRef.destroy();
			}
		}
		this.handlers = {};
	}

	/**
	 * Generates a key for a ActivatedRouteSnapshot which consists of all of its url segments.
	 * Example: URL '/roadwork/1' generates key 'roadwork/1'.
	 */
	private getKey(route: ActivatedRouteSnapshot): string {
		const routeWithoutQueryParams = route.pathFromRoot
			.map((route) => route.url)
			.filter((segments) => segments.length > 0)
			.join("/");
		return routeWithoutQueryParams.concat(
			route.queryParams[ASSET_ID_ROUTE_PARAM_NAME] ?? "",
			route.queryParams[ROADWORK_ID_ROUTE_PARAM_NAME] ?? "",
		);
	}
}
